// ABOUTME: Matemática de calibración: pasa píxeles de la cámara al espacio local de la cabeza
// ABOUTME: y ajusta el elipsoide del cráneo a las medidas de la silueta (cm).
import { Matrix4, Vector3 } from "three";
import type { HeadModel, Vec3 } from "../dreads/types";

export interface Pixel {
  x: number;
  y: number;
}
export interface Size {
  width: number;
  height: number;
}

/** Centímetros que mide un píxel a la profundidad dada (cámara pinhole con FOV vertical). */
export function cmPerPixel(depthCm: number, fovDeg: number, viewportHeightPx: number): number {
  return (2 * depthCm * Math.tan((fovDeg / 2) * (Math.PI / 180))) / viewportHeightPx;
}

/**
 * Proyecta un píxel sobre el plano paralelo a la cámara que pasa por el origen de la
 * cabeza y devuelve el punto en coordenadas locales de la cabeza (cm).
 */
export function screenToHeadSpace(px: Pixel, video: Size, fovDeg: number, pose: number[]): Vec3 {
  const m = new Matrix4().fromArray(pose);
  const origin = new Vector3().setFromMatrixPosition(m);
  const depth = -origin.z;
  const cm = cmPerPixel(depth, fovDeg, video.height);
  const camX = (px.x - video.width / 2) * cm;
  const camY = (video.height / 2 - px.y) * cm;
  const cameraPoint = new Vector3(camX, camY, origin.z);
  const local = cameraPoint.applyMatrix4(m.clone().invert());
  return [local.x, local.y, local.z];
}

export interface SilhouetteCm {
  /** Altura de la coronilla (pelo incluido) en espacio de cabeza. */
  topY: number;
  leftX: number;
  rightX: number;
}

/** Parte baja del cráneo en el modelo canónico: la mandíbula no la medimos, la fijamos. */
const JAW_Y = -8;
/** Cara delantera del elipsoide: justo detrás de la punta de la nariz (z=7.5). */
const FRONT_Z = 6.8;
/** Profundidad típica de una cabeza respecto a su anchura. */
const DEPTH_TO_WIDTH = 1.22;

export function fitHeadFromSilhouette(s: SilhouetteCm): HeadModel {
  const rx = (s.rightX - s.leftX) / 2;
  const ry = (s.topY - JAW_Y) / 2;
  if (rx < 4 || rx > 14) throw new Error(`Anchura de cabeza fuera de rango: ${(rx * 2).toFixed(1)} cm`);
  if (ry < 7 || ry > 16) throw new Error(`Altura de cabeza fuera de rango: ${(ry * 2).toFixed(1)} cm`);
  const rz = rx * DEPTH_TO_WIDTH;
  return {
    center: [(s.leftX + s.rightX) / 2, s.topY - ry, FRONT_Z - rz],
    radii: [rx, ry, rz],
  };
}

/** Proyecta un punto local de la cabeza (cm) a píxeles de la imagen de la cámara. */
export function projectHeadToScreen(local: Vec3, video: Size, fovDeg: number, pose: number[]): Pixel {
  const m = new Matrix4().fromArray(pose);
  const cam = new Vector3(local[0], local[1], local[2]).applyMatrix4(m);
  const depth = -cam.z;
  const cm = cmPerPixel(depth, fovDeg, video.height);
  return { x: video.width / 2 + cam.x / cm, y: video.height / 2 - cam.y / cm };
}
